import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { Frame } from '../momentum-v3/primitives';
import { sanitizeForgeSvg } from './sanitizeSvg';
import { ForgeDebugPanel, isForgeDebugRequested } from './ForgeDebugPanel';

/**
 * Build Engine Selector v1, Forge web renderer. A Forge-built section
 * carries a self-contained SVG (`section.forge.svg`, already sanitized and
 * schema-validated server-side — see `src/build-engines/forge/`) authored
 * inside the same 1920x1080 momentum_v3 frame every other engine uses (32px
 * inset, 128px footer band left empty for the chrome below; repointed from
 * momentum_v2 when momentum_v1/v2 were sunset from the web app). This mounts
 * the same `Frame` chrome (rounded card + footer, page number, agency/
 * client lockup) every Studio/momentum_v3 archetype mounts, then draws the
 * SVG verbatim in an absolutely positioned canvas layer on top — nothing
 * here re-invokes the model; export/presentation just replay this same
 * markup (mirrors `StudioPage.tsx`).
 *
 * Read-only rendering only in v1 — no inline editing of SVG text/values.
 */

/**
 * `surface` isn't yet an explicit field on `ForgeSectionState` — same gap
 * `StudioPage.inferSurface` documents for Studio. Cover/thesis/close slides
 * (spec's hero-treatment composition types, `CompositionSlideType` in
 * `src/composition/types.ts`) and any slide the model itself flagged
 * `treatment: 'hero'` render on the dark card; everything else light.
 */
function inferSurface(section: PageProps['section']): 'dark' | 'light' {
  const brief = section.forge?.brief;
  if (brief?.treatment === 'hero') return 'dark';
  if (brief?.slideType === 'cover' || brief?.slideType === 'thesis' || brief?.slideType === 'close') {
    return 'dark';
  }
  return 'light';
}

export function ForgePage(props: PageProps): ReactElement | null {
  const { proposal, section, actions, readOnly } = props;
  const svg = section.forge?.svg;
  // A Forge section that fell back (no SVG survived validation) carries no
  // svg — return null so the registry's fallback dispatch (studio-safe /
  // classic-fallback) handles the section instead, same contract as
  // StudioPage returning null when `studio.resolved` is absent.
  if (!svg) return null;

  const safeSvg = sanitizeForgeSvg(svg);
  const surface = inferSurface(section);

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv2-forge-root"
      hideTitle
    >
      <Frame surface={surface} proposal={proposal} section={section}>
        {null}
      </Frame>
      <div
        className="tpl-forge-canvas"
        data-slide-id={section.id}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeSvg }}
      />
      {!readOnly && isForgeDebugRequested() ? <ForgeDebugPanel proposal={proposal} section={section} /> : null}
    </PageShell>
  );
}
